import { useState } from 'react';
import { useChatStore } from '../../store/chatStore';
import { X, FileText, ExternalLink, FolderOpen, Loader2 } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

/**
 * Side panel that displays detailed metadata for a selected source reference,
 * including document title, section, file path, relevance score, and an action to open the document.
 */
export function SourcePanel() {
  const { selectedSource, setSelectedSource } = useChatStore();
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  if (!selectedSource) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <FolderOpen size={48} className="text-gray-300 mb-4" />
        <h3 className="font-medium text-gray-600 mb-2">No Source Selected</h3>
        <p className="text-sm text-gray-400">
          Click on a source citation to view its details here.
        </p>
      </div>
    );
  }

  const relevancePercentage = Math.round(selectedSource.relevance_score * 100);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <FileText size={18} />
          Source Details
        </h3>
        <button
          onClick={() => setSelectedSource(null)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Close source panel"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Document title */}
        <div className="mb-6">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Document
          </label>
          <h4 className="text-lg font-medium text-gray-800 mt-1">
            {selectedSource.title}
          </h4>
        </div>

        {/* Section */}
        {selectedSource.section && (
          <div className="mb-6">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Section
            </label>
            <p className="text-gray-700 mt-1">{selectedSource.section}</p>
          </div>
        )}

        {/* File path */}
        <div className="mb-6">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Source Path
          </label>
          <p className="text-sm text-gray-600 mt-1 font-mono bg-gray-50 p-2 rounded break-all">
            {selectedSource.path}
          </p>
        </div>

        {/* Relevance score */}
        <div className="mb-6">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Relevance Score
          </label>
          <div className="mt-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${relevancePercentage}%` }}
                />
              </div>
              <span className="text-lg font-semibold text-gray-800">
                {relevancePercentage}%
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {relevancePercentage >= 80
                ? 'High confidence match'
                : relevancePercentage >= 50
                ? 'Moderate confidence match'
                : 'Low confidence match'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={async () => {
              setIsLoadingUrl(true);
              setUrlError(null);
              try {
                const response = await fetchApi(`${API_BASE}/document/signed-url`, {
                  method: 'POST',
                  headers: await getAuthHeaders(),
                  body: JSON.stringify({ path: selectedSource.path }),
                });
                if (!response.ok) {
                  const error = await response.json();
                  throw new Error(error.detail || 'Failed to get document URL');
                }
                const data = await response.json();
                window.open(data.signed_url, '_blank', 'noopener,noreferrer');
              } catch (error) {
                console.error('Error getting signed URL:', error);
                setUrlError(error instanceof Error ? error.message : 'Failed to open document');
              } finally {
                setIsLoadingUrl(false);
              }
            }}
            disabled={isLoadingUrl}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingUrl ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <ExternalLink size={16} />
                Open Document
              </>
            )}
          </button>
          {urlError && (
            <p className="text-sm text-red-500 text-center">{urlError}</p>
          )}
        </div>
      </div>
    </div>
  );
}
